import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertData, AlertProps } from '@netiva/classifieds-ui';

import { useAppSelector } from '@/store';

type PlatformNotice = {
  allowedCountries: string[];
  titleKey: 'ad.notice.agropool.title' | 'ad.notice.tierinserate.title';
  descriptionKey: 'ad.notice.agropool.description' | 'ad.notice.tierinserate.description';
};

/** Platforms that only accept providers from certain countries, keyed by the lower-case platform key. */
const platformNotices: Record<string, PlatformNotice> = {
  agropool: {
    allowedCountries: ['CH'],
    titleKey: 'ad.notice.agropool.title',
    descriptionKey: 'ad.notice.agropool.description',
  },
  tierinserate: {
    allowedCountries: ['CH', 'LI', 'DE', 'AT', 'FR', 'IT'],
    titleKey: 'ad.notice.tierinserate.title',
    descriptionKey: 'ad.notice.tierinserate.description',
  },
};

type AdNoticeProps = Omit<AlertProps, 'alert'>;

/** Notice about the country restriction of the selected platform, shown to accounts outside its allowed countries. */
export const AdNotice: FC<AdNoticeProps> = (props) => {
  const { t } = useTranslation();
  const { account } = useAppSelector((state) => state.account);
  const { platform } = useAppSelector((state) => state.ad);
  const countryIso2 = account?.address?.countryIso2;
  const platformNotice = platform ? platformNotices[platform.toLowerCase()] : undefined;

  if (!countryIso2 || !platformNotice || platformNotice.allowedCountries.includes(countryIso2)) {
    return null;
  }

  const notice: AlertData = {
    status: 'info',
    title: t(platformNotice.titleKey),
    description: t(platformNotice.descriptionKey),
  };

  return <Alert alert={notice} {...props} />;
};
